import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Link,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Email,
  Phone,
  LocationOn,
  GitHub,
} from '@mui/icons-material';

const NewFooter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const footerBg = isDark
    ? alpha('#0f172a', 0.98)
    : alpha('#f8fafc', 0.98);

  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const mutedColor = isDark ? alpha('#f1f5f9', 0.6) : alpha('#0f172a', 0.6);
  const borderColor = isDark ? alpha('#374151', 0.3) : alpha('#cbd5e1', 0.3);

  const footerLinks = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'How It Works', href: '#howitworks' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '#' },
        { label: 'Contact Us', href: '#' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '#' },
        { label: 'Terms of Service', href: '#' },
      ],
    },
  ];

  const socialLinks = [
    { icon: <Twitter />, href: '#', label: 'Twitter' },
    { icon: <LinkedIn />, href: '#', label: 'LinkedIn' },
  ];

  return (
    <Box
      sx={{
        background: footerBg,
        borderTop: `1px solid ${borderColor}`,
        pt: { xs: 6, md: 10 },
        pb: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    background: primaryGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: '"Sora", "Inter", sans-serif',
                    mb: 2,
                  }}
                >
                  Carrier Finder Pro
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: mutedColor,
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  Professional logistics platform with FMCSA carrier search, automated campaigns, and real-time analytics.
                </Typography>
              </Box>

              {/* Contact Info */}
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Email sx={{ fontSize: 20, color: mutedColor }} />
                  <Link
                    href="mailto:support@ecarrierfinderpro.com"
                    sx={{
                      color: mutedColor,
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      '&:hover': { color: textColor },
                    }}
                  >
                    support@ecarrierfinderpro.com
                  </Link>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Phone sx={{ fontSize: 20, color: mutedColor }} />
                  <Link
                    href="tel:+923456411892"
                    sx={{
                      color: mutedColor,
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      '&:hover': { color: textColor },
                    }}
                  >
                    +92 345 6411892
                  </Link>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOn sx={{ fontSize: 20, color: mutedColor, mt: 0.3 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: mutedColor,
                      fontSize: '0.9rem',
                    }}
                  >
                    123 Business St, Suite 100<br />
                    San Francisco, CA 94105
                  </Typography>
                </Stack>
              </Stack>

              {/* Social Links */}
              <Stack direction="row" spacing={1}>
                {socialLinks.map((social, idx) => (
                  <IconButton
                    key={idx}
                    href={social.href}
                    aria-label={social.label}
                    sx={{
                      color: mutedColor,
                      border: `1px solid ${borderColor}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        background: primaryGradient,
                        color: '#fff',
                        borderColor: 'transparent',
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Footer Links */}
          {footerLinks.map((section, idx) => (
            <Grid item xs={6} sm={6} md={2} key={idx}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: textColor,
                  mb: 2,
                  fontFamily: '"Sora", "Inter", sans-serif',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.5}>
                {section.links.map((link, linkIdx) => (
                  <Link
                    key={linkIdx}
                    href={link.href}
                    sx={{
                      color: mutedColor,
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: textColor,
                        paddingLeft: 1,
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: borderColor, mb: 4 }} />

        {/* Bottom Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body2"
            sx={{
              color: mutedColor,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            © {new Date().getFullYear()} Carrier Finder Pro. All rights reserved. Built for freight professionals.
          </Typography>

          <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
            <Link
              href="#"
              sx={{
                color: mutedColor,
                textDecoration: 'none',
                fontSize: '0.85rem',
                '&:hover': { color: textColor },
              }}
            >
              Privacy
            </Link>
            <Link
              href="#"
              sx={{
                color: mutedColor,
                textDecoration: 'none',
                fontSize: '0.85rem',
                '&:hover': { color: textColor },
              }}
            >
              Terms
            </Link>
            <Link
              href="#"
              sx={{
                color: mutedColor,
                textDecoration: 'none',
                fontSize: '0.85rem',
                '&:hover': { color: textColor },
              }}
            >
              Cookies
            </Link>
            <Link
              href="#"
              sx={{
                color: mutedColor,
                textDecoration: 'none',
                fontSize: '0.85rem',
                '&:hover': { color: textColor },
              }}
            >
              Sitemap
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default NewFooter;
